import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import Global from '../../../store/global-selectors';
import { CarrierConnectionInteractionAction, CarrierConnectionInteractionSection, Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import type { ValidationError } from '../../../validation/validation-error';
import { messages as rootMessages } from '../messages';
import type { Address, WwexFormData } from '../types';
import { BILLING_COMPANY_NAME_FIELD, BILLING_FULL_NAME_FIELD, buildBillingAddressSchema } from '../wwex-form-validation-schema';

import { BillingAddressForm } from './billing-address-form';
import { messages } from './edit-billing-address-view.messages';
import { WwexClearAllLink } from './wwex-clear-all-link';
import { WwexSubViewFooter } from './wwex-sub-view-footer';
import { WwexSubViewHeader } from './wwex-sub-view-header';
import style from './edit-billing-address-view.module.css';
import modalStyle from '../wwex-registration-modal.module.less';

const EMPTY_BILLING_ADDRESS: Address = { line1: '', city: '', state: '', postalCode: '', countryCode: 'US' };

const ADDRESS_COMPARISON_FIELDS = ['line1', 'line2', 'city', 'state', 'postalCode', 'countryCode'] as const;

const pickAddressFields = (address?: Partial<Address> | null): Record<string, string> =>
  ADDRESS_COMPARISON_FIELDS.reduce((acc, field) => ({ ...acc, [field]: address?.[field] ?? '' }), {} as Record<string, string>);

type EditBillingAddressViewProps = {
  headerId: string;
  formData: Partial<WwexFormData>;
  isDisabled: boolean;
  onSave: (data: Partial<WwexFormData>) => void;
  onBack: () => void;
  onCancel: () => void;
};

export const EditBillingAddressView = ({ headerId, formData, isDisabled, onSave, onBack, onCancel }: EditBillingAddressViewProps) => {
  const [draft, setDraft] = useState({
    fullName: formData.billingFullName ?? '',
    companyName: formData.billingCompanyName ?? '',
    address: formData.billingAddress ?? EMPTY_BILLING_ADDRESS,
  });
  const [showAllErrors, setShowAllErrors] = useState(false);

  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);
  const defaultShipFrom = useSelector(Global.selectSellerDefaultShipFrom);
  const initialDraftRef = useRef(draft);
  const editCountRef = useRef(0);

  useEffect(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.BILLING_ADDRESS,
      action: CarrierConnectionInteractionAction.EDIT_OPENED,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- fire once when this sub-view mounts
  }, []);

  const schema = useMemo(() => buildBillingAddressSchema(), []);
  const validationForm = useMemo(
    () => ({
      [BILLING_FULL_NAME_FIELD]: draft.fullName,
      [BILLING_COMPANY_NAME_FIELD]: draft.companyName,
      ...draft.address,
    }),
    [draft],
  );
  const { errors, isValid } = useYupFormValidation({ form: validationForm, schema });

  const handleFieldChange = useCallback((field: string, value: string | Address) => {
    editCountRef.current += 1;
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClearAll = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.BILLING_ADDRESS,
      action: CarrierConnectionInteractionAction.CLEARED_ALL,
    });
    setDraft({ fullName: '', companyName: '', address: EMPTY_BILLING_ADDRESS });
  }, [trackCarrierConnectionStep]);

  const handleSave = useCallback(() => {
    if (!isValid) {
      setShowAllErrors(true);
      return;
    }

    const fieldsChanged = [
      draft.fullName !== initialDraftRef.current.fullName ? 'billingFullName' : null,
      draft.companyName !== initialDraftRef.current.companyName ? 'billingCompanyName' : null,
      !isEqual(pickAddressFields(draft.address), pickAddressFields(initialDraftRef.current.address)) ? 'billingAddress' : null,
    ].filter(Boolean);

    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.BILLING_ADDRESS,
      action: CarrierConnectionInteractionAction.SAVED,
      fields_changed: fieldsChanged,
      edit_count: editCountRef.current,
      billing_address_matches_default_ship_from: isEqual(
        pickAddressFields(draft.address),
        pickAddressFields(defaultShipFrom?.originAddress),
      ),
      billing_address_edited: fieldsChanged.length > 0,
    });

    onSave({
      ...formData,
      billingFullName: draft.fullName,
      billingCompanyName: draft.companyName,
      billingAddress: draft.address,
    });
  }, [draft, formData, isValid, onSave, trackCarrierConnectionStep, defaultShipFrom]);

  const handleCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.BILLING_ADDRESS,
      action: CarrierConnectionInteractionAction.CANCELLED,
    });
    onCancel();
  }, [onCancel, trackCarrierConnectionStep]);

  const handleBack = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.BILLING_ADDRESS,
      action: CarrierConnectionInteractionAction.WENT_BACK,
    });
    onBack();
  }, [onBack, trackCarrierConnectionStep]);

  return (
    <>
      <ModalHeader className={modalStyle.modalHeader} closeModal={handleCancel} id={headerId}>
        <WwexSubViewHeader isDisabled={isDisabled} onBack={handleBack} titleMessage={rootMessages.editBillingAddressTitle} />
      </ModalHeader>

      <ModalBody className={modalStyle.modalBody}>
        <FlexColumn gap={Gap.Space16}>
          <FlexRow className={style.sectionHeaderRow} gap={Gap.Space8}>
            <Text textType={TextType.H4} value={messages.sectionTitle} />
            <WwexClearAllLink isDisabled={isDisabled} onClick={handleClearAll} />
          </FlexRow>

          <BillingAddressForm
            data={draft}
            errors={errors as Record<string, ValidationError>}
            isDisabled={isDisabled}
            onBlur={noop}
            onChange={handleFieldChange}
            showAllErrors={showAllErrors}
          />
        </FlexColumn>
      </ModalBody>

      <ModalFooter>
        <WwexSubViewFooter
          isDisabled={isDisabled}
          isSaveDisabled={!isValid}
          onBack={handleBack}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      </ModalFooter>
    </>
  );
};
